import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPage.css';

const TermsOfService: React.FC = () => {
    return (
        <div className="policy-page">
            <nav className="policy-nav">
                <Link to="/" className="logo">Commerium</Link>
            </nav>

            <main className="policy-content">
                <h1>Terms of Service</h1>
                <p className="last-updated">Last updated: January 20, 2026</p>

                <section>
                    <h2>1. Agreement to Terms</h2>
                    <p>
                        By accessing or using Commerium ("the Platform"), you agree to be bound by these Terms of
                        Service ("Terms"). If you disagree with any part of these terms, you may not access the Platform.
                    </p>
                    <p>
                        These Terms apply to all visitors, users, and others who access or use the Platform,
                        whether through our website, mobile application, or API.
                    </p>
                </section>

                <section>
                    <h2>2. Description of Service</h2>
                    <p>
                        Commerium is an AI-powered e-commerce platform designed for artisans and makers. Our services include:
                    </p>
                    <ul>
                        <li>AI-generated product listings from images</li>
                        <li>Inventory and raw materials management</li>
                        <li>Multi-channel marketplace integration</li>
                        <li>Business analytics and reporting</li>
                        <li>AI-powered customer service tools</li>
                    </ul>
                </section>

                <section>
                    <h2>3. Account Registration</h2>
                    <h3>3.1 Eligibility</h3>
                    <p>
                        You must be at least 16 years old and capable of forming a binding contract to use the Platform.
                        By registering, you represent that you meet these requirements.
                    </p>

                    <h3>3.2 Account Security</h3>
                    <p>
                        You are responsible for maintaining the confidentiality of your account credentials and for all
                        activities that occur under your account. You agree to notify us immediately of any unauthorized use.
                    </p>

                    <h3>3.3 Account Information</h3>
                    <p>
                        You agree to provide accurate, current, and complete information during registration and to
                        update such information to keep it accurate and current.
                    </p>
                </section>

                <section>
                    <h2>4. Subscription and Payment</h2>
                    <h3>4.1 Plans and Pricing</h3>
                    <p>
                        Commerium offers various subscription plans. Pricing is displayed on our website and may be
                        updated from time to time. Price changes will be communicated 30 days in advance.
                    </p>

                    <h3>4.2 Billing</h3>
                    <p>
                        Subscriptions are billed in advance on a monthly or annual basis. Payment is processed securely
                        through Shopify. You authorize us to charge your Shopify account at the start of each billing period.
                    </p>

                    <h3>4.3 Free Trial</h3>
                    <p>
                        New users may be eligible for a free trial. At the end of the trial, you will be billed unless
                        you cancel before the trial ends. No credit card is required to start a trial.
                    </p>

                    <h3>4.4 Refunds</h3>
                    <p>
                        Refunds may be requested within 14 days of the initial subscription purchase. After this period,
                        payments are non-refundable. Contact support@trycommerium.com for refund requests.
                    </p>

                    <h3>4.5 Cancellation</h3>
                    <p>
                        You may cancel your subscription at any time through your account settings. Upon cancellation,
                        you will retain access until the end of your current billing period.
                    </p>
                </section>

                <section>
                    <h2>5. User Content</h2>
                    <h3>5.1 Ownership</h3>
                    <p>
                        You retain all ownership rights to content you upload, including product images, descriptions,
                        and business data. By uploading content, you grant us a limited license to process, store,
                        and display it as necessary to provide our services.
                    </p>

                    <h3>5.2 Responsibility</h3>
                    <p>
                        You are solely responsible for the content you upload and listings you publish. Content must
                        not violate any laws, infringe intellectual property rights, or contain prohibited materials.
                    </p>

                    <h3>5.3 AI-Generated Content</h3>
                    <p>
                        Our AI generates listing content based on your inputs. While we strive for accuracy, you are
                        responsible for reviewing and editing AI-generated content before publishing. We are not liable
                        for errors in AI-generated descriptions.
                    </p>
                </section>

                <section>
                    <h2>6. Marketplace Integrations</h2>
                    <p>
                        When you connect third-party marketplaces (Etsy, Shopify, Amazon, etc.) to Commerium:
                    </p>
                    <ul>
                        <li>You authorize us to access and manage listings on your behalf</li>
                        <li>You remain bound by each marketplace's terms of service</li>
                        <li>We are not responsible for actions taken by third-party marketplaces</li>
                        <li>Marketplace fees and policies are separate from Commerium</li>
                    </ul>
                </section>

                <section>
                    <h2>7. Prohibited Uses</h2>
                    <p>You agree not to:</p>
                    <ul>
                        <li>Use the Platform for any unlawful purpose</li>
                        <li>Violate any applicable laws or regulations</li>
                        <li>Upload content that infringes on others' intellectual property</li>
                        <li>Attempt to gain unauthorized access to our systems</li>
                        <li>Interfere with or disrupt the Platform's operation</li>
                        <li>Use automated tools to scrape or extract data</li>
                        <li>Resell or redistribute our services without authorization</li>
                        <li>Upload malicious code or content</li>
                    </ul>
                </section>

                <section>
                    <h2>8. Intellectual Property</h2>
                    <p>
                        The Platform, including its original content, features, and functionality, is owned by
                        Commerium Technologies and is protected by international copyright, trademark, and other
                        intellectual property laws.
                    </p>
                    <p>
                        "Commerium" and our logo are trademarks of Commerium Technologies. You may not use our
                        trademarks without prior written consent.
                    </p>
                </section>

                <section>
                    <h2>9. Limitation of Liability</h2>
                    <p>
                        To the maximum extent permitted by law, Commerium shall not be liable for any indirect,
                        incidental, special, consequential, or punitive damages, including but not limited to:
                    </p>
                    <ul>
                        <li>Loss of profits, revenue, or data</li>
                        <li>Business interruption</li>
                        <li>Errors in AI-generated content</li>
                        <li>Third-party marketplace actions or policies</li>
                        <li>Unauthorized access to your account</li>
                    </ul>
                    <p>
                        Our total liability shall not exceed the amount you paid us in the 12 months preceding
                        the claim.
                    </p>
                </section>

                <section>
                    <h2>10. Disclaimer of Warranties</h2>
                    <p>
                        The Platform is provided "as is" and "as available" without any warranties of any kind,
                        either express or implied, including but not limited to:
                    </p>
                    <ul>
                        <li>Merchantability or fitness for a particular purpose</li>
                        <li>Accuracy or completeness of AI-generated content</li>
                        <li>Uninterrupted or error-free operation</li>
                        <li>Security of data transmission</li>
                    </ul>
                </section>

                <section>
                    <h2>11. Indemnification</h2>
                    <p>
                        You agree to defend, indemnify, and hold harmless Commerium and its officers, directors,
                        employees, and agents from any claims, damages, or expenses arising from:
                    </p>
                    <ul>
                        <li>Your use of the Platform</li>
                        <li>Content you upload or publish</li>
                        <li>Violation of these Terms</li>
                        <li>Infringement of third-party rights</li>
                    </ul>
                </section>

                <section>
                    <h2>12. Termination</h2>
                    <p>
                        We may terminate or suspend your account immediately, without prior notice, for conduct
                        that we believe violates these Terms or is harmful to other users or the Platform.
                    </p>
                    <p>
                        Upon termination, your right to use the Platform will cease immediately. Provisions that
                        by their nature should survive termination shall survive.
                    </p>
                </section>

                <section>
                    <h2>13. Governing Law</h2>
                    <p>
                        These Terms shall be governed by and construed in accordance with the laws of Spain,
                        without regard to its conflict of law provisions. Any disputes shall be resolved in the
                        courts of Madrid, Spain.
                    </p>
                </section>

                <section>
                    <h2>14. Changes to Terms</h2>
                    <p>
                        We reserve the right to modify these Terms at any time. We will provide notice of significant
                        changes via email or through the Platform. Continued use after changes constitutes acceptance.
                    </p>
                </section>

                <section>
                    <h2>15. Contact Us</h2>
                    <p>For questions about these Terms, contact us at:</p>
                    <ul>
                        <li>Email: legal@trycommerium.com</li>
                        <li>Address: Commerium Technologies, Calle Mayor 1, 28001 Madrid, Spain</li>
                    </ul>
                </section>
            </main>

            <footer className="policy-footer">
                <Link to="/">← Back to Home</Link>
                <p>© 2026 Commerium. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default TermsOfService;
